// loginError.js
// Turns a failed `POST /Authentication/login` into the text the login alert shows.
//
// Why this file exists: Login.js used to catch the rejection and do nothing but
// `setInvalidLogin(true)`, so *every* failure — a wrong password (the API answers 200 with an
// empty body), a 500 from an unreachable database, or a request that never left the browser —
// rendered the same "Authentication failed" alert with an empty description. That is how a MySQL
// connect timeout on the API side reaches the user as "my password is wrong", and it cost a
// debugging session to identify. The mapping lives in its own module so it can be pinned by a
// unit test without rendering antd.

export const AUTHENTICATION_FAILED_TITLE = 'Authentication failed';
export const SIGN_IN_UNAVAILABLE_TITLE = 'Sign in unavailable';
export const CANNOT_REACH_SERVER_TITLE = 'Cannot reach the server';

export const INVALID_CREDENTIALS_MESSAGE = 'Your authentication information is invalid';
export const ALREADY_LOGGED_IN_MESSAGE = 'Your account is already logged in.';
export const EMPTY_CREDENTIALS_MESSAGE = 'Enter your username and password to sign in.';

// The API reports a 500 when it cannot open its MySQL connection (MySqlConnector's
// "Connect Timeout expired" surfaces as an unhandled exception), which is a server fault the
// user cannot fix by retyping a password — say so, and point at the administrator.
const SERVER_ERROR_MESSAGE =
   'The sign-in server is temporarily unavailable — it could not reach its database. ' +
   'Please try again in a few minutes; if it keeps happening, contact your school administrator.';
const NETWORK_ERROR_MESSAGE = 'Cannot reach the sign-in server. Check your internet connection and try again.';
const TIMEOUT_MESSAGE = 'The sign-in server took too long to respond. Please try again.';
const RATE_LIMIT_MESSAGE = 'Too many sign-in attempts. Please wait a moment and try again.';
const UNEXPECTED_MESSAGE = 'Something went wrong while signing in. Please try again.';

/**
 * `{ title, description }` for the login alert.
 *
 * `error.response` is undefined when the request never completed (server down, wrong host, TLS
 * certificate rejected, CORS block, offline), which is why it is checked before the status.
 */
export const getLoginError = (error) => {
   const response = error ? error.response : undefined;

   if (!response) {
      const timedOut = Boolean(error) && error.code === 'ECONNABORTED';

      return {
         title: CANNOT_REACH_SERVER_TITLE,
         description: timedOut ? TIMEOUT_MESSAGE : NETWORK_ERROR_MESSAGE,
      };
   }

   const status = Number(response.status);

   if (status === 429) {
      return { title: SIGN_IN_UNAVAILABLE_TITLE, description: RATE_LIMIT_MESSAGE };
   }

   if (status >= 500) {
      return { title: SIGN_IN_UNAVAILABLE_TITLE, description: SERVER_ERROR_MESSAGE };
   }

   if (status >= 400) {
      return { title: AUTHENTICATION_FAILED_TITLE, description: INVALID_CREDENTIALS_MESSAGE };
   }

   // A 2xx/3xx rejection means the response body was not the JWT this screen expects.
   return { title: AUTHENTICATION_FAILED_TITLE, description: UNEXPECTED_MESSAGE };
};
